import React, { useEffect, useState, createContext, useContext, memo } from 'react';
export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  category: string;
  subcategory?: string;
  image: string;
  images?: string[];
  description: string;
  specs?: Record<string, string>;
  featured?: boolean;
  inStock: boolean;
  rating?: number;
  reviewCount?: number;
}
export interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  subcategories?: {
    id: string;
    name: string;
  }[];
}
interface ProductContextType {
  products: Product[];
  categories: Category[];
  featuredProducts: Product[];
  getProductsByCategory: (category: string) => Product[];
  getProductById: (id: string) => Product | undefined;
  searchProducts: (query: string) => Product[];
  filterProducts: (filters: Record<string, any>) => Product[];
}
// Sample data
const sampleCategories: Category[] = [{
  id: 'electronics',
  name: 'Electronics',
  icon: 'laptop',
  description: 'Laptops, phones, cameras & more'
}, {
  id: 'gold',
  name: 'Gold',
  icon: 'circle-dollar-sign',
  description: 'Bars, coins & jewelry'
}, {
  id: 'silver',
  name: 'Silver',
  icon: 'circle-dollar-sign',
  description: 'Investment grade silver'
}, {
  id: 'platinum',
  name: 'Platinum',
  icon: 'circle-dollar-sign',
  description: 'Rare platinum items'
}, {
  id: 'technical-tools',
  name: 'Technical Tools',
  icon: 'tool',
  description: 'Professional tech equipment'
}, {
  id: 'accessories',
  name: 'Accessories',
  icon: 'headphones',
  description: 'Tech accessories & add-ons'
}];
const sampleProducts: Product[] = [{
  id: 'macbook-pro-2021',
  name: 'MacBook Pro 2021',
  price: 1299,
  originalPrice: 1499,
  discount: 13,
  category: 'electronics',
  subcategory: 'laptops',
  image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1626&q=80',
  images: ['https://images.unsplash.com/photo-1517336714731-489689fd1ca8?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1626&q=80', 'https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'],
  description: 'Refurbished MacBook Pro with 8-core CPU and 14-core GPU, 16GB RAM, 512GB SSD',
  specs: {
    Processor: 'Apple M1 Pro chip with 8‑core CPU and 14‑core GPU',
    Memory: '16GB unified memory',
    Storage: '512GB SSD',
    Display: '14-inch Liquid Retina XDR display',
    Battery: 'Up to 17 hours of battery life'
  },
  featured: true,
  inStock: true,
  rating: 4.8,
  reviewCount: 127
}, {
  id: 'iphone-13-pro',
  name: 'iPhone 13 Pro',
  price: 999,
  category: 'electronics',
  subcategory: 'phones',
  image: 'https://images.unsplash.com/photo-1632661674596-df8be070a5c5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
  description: 'Refurbished iPhone 13 Pro with A15 Bionic chip, Pro camera system, and Super Retina XDR display',
  featured: true,
  inStock: true,
  rating: 4.7,
  reviewCount: 98
}, {
  id: 'gold-bar-1oz',
  name: 'Gold Bar 1oz',
  price: 2084,
  category: 'gold',
  image: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
  description: 'Investment grade 1oz gold bar, 99.99% pure gold',
  featured: true,
  inStock: true,
  rating: 5.0,
  reviewCount: 42
}, {
  id: 'silver-eagle-2023',
  name: 'Silver Eagle 2023',
  price: 35,
  category: 'silver',
  image: 'https://images.unsplash.com/photo-1618403088890-3d9ff6f4c8b1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
  description: 'American Silver Eagle coin, 2023 edition, 1oz of 99.9% pure silver',
  inStock: true,
  rating: 4.9,
  reviewCount: 67
}, {
  id: 'canon-eos-r6',
  name: 'Canon EOS R6',
  price: 2499,
  originalPrice: 2699,
  discount: 7,
  category: 'electronics',
  subcategory: 'cameras',
  image: 'https://images.unsplash.com/photo-1621520291095-aa6c7137f048?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
  description: 'Full-frame mirrorless camera with 20MP sensor and 4K video recording',
  inStock: true,
  rating: 4.8,
  reviewCount: 53
}, {
  id: 'platinum-bar-1oz',
  name: 'Platinum Bar 1oz',
  price: 1045,
  category: 'platinum',
  image: 'https://images.unsplash.com/photo-1624365169198-f193658564de?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
  description: 'Investment grade 1oz platinum bar, 99.95% pure platinum',
  inStock: true,
  rating: 4.9,
  reviewCount: 18
}];
const ProductContext = createContext<ProductContextType | undefined>(undefined);
export const ProductProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [products] = useState<Product[]>(sampleProducts);
  const [categories] = useState<Category[]>(sampleCategories);
  const featuredProducts = products.filter(product => product.featured);
  const getProductsByCategory = (category: string) => {
    return products.filter(product => product.category === category);
  };
  const getProductById = (id: string) => {
    return products.find(product => product.id === id);
  };
  const searchProducts = (query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return products.filter(product => product.name.toLowerCase().includes(lowercaseQuery) || product.description.toLowerCase().includes(lowercaseQuery));
  };
  const filterProducts = (filters: Record<string, any>) => {
    return products.filter(product => {
      let matches = true;
      if (filters.category && filters.category !== 'all') {
        matches = matches && product.category === filters.category;
      }
      if (filters.minPrice !== undefined) {
        matches = matches && product.price >= filters.minPrice;
      }
      if (filters.maxPrice !== undefined) {
        matches = matches && product.price <= filters.maxPrice;
      }
      if (filters.inStock !== undefined) {
        matches = matches && product.inStock === filters.inStock;
      }
      return matches;
    });
  };
  return <ProductContext.Provider value={{
    products,
    categories,
    featuredProducts,
    getProductsByCategory,
    getProductById,
    searchProducts,
    filterProducts
  }} data-id="element-286">
      {children}
    </ProductContext.Provider>;
};
export const useProducts = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};